import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Slider from 'react-slick';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import MediaFrame from '../components/MediaFrame';
import { backgroundOverlay, backgroundStyle, backgroundValue, cleanHtml } from '../utils/presentation';
import { imageUrl } from '../utils/setModel';
import './LoreSlidesModule.css';

function StoryArrow({ className = '', onClick, direction }) {
  const Icon = direction === 'previous' ? BsChevronLeft : BsChevronRight;
  return <button type="button" className={`${className} set-story-arrow set-is-${direction}`} onClick={onClick} aria-label={`${direction === 'previous' ? 'Previous' : 'Next'} story slide`}><Icon /></button>;
}

function useTallestSlide(features, crop) {
  const stageRef = useRef(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    let frame = 0;
    let disposed = false;

    const measure = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        if (disposed) return;
        const slides = [...stage.querySelectorAll('.set-story-slide')];
        const next = Math.ceil(Math.max(0, ...slides.map((slide) => slide.getBoundingClientRect().height)));
        setHeight((current) => current === next ? current : next);
      });
    };

    const slides = [...stage.querySelectorAll('.set-story-slide')];
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    slides.forEach((slide) => observer?.observe(slide));
    stage.querySelectorAll('img').forEach((image) => {
      if (!image.complete) image.addEventListener('load', measure);
    });
    window.addEventListener('resize', measure);
    if (document.fonts?.ready) document.fonts.ready.then(measure).catch(() => undefined);
    measure();

    return () => {
      disposed = true;
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      stage.querySelectorAll('img').forEach((image) => image.removeEventListener('load', measure));
    };
  }, [features, crop]);

  return [stageRef, height];
}

export default function LoreSlidesModule({ module, onImageClick }) {
  const { features = [] } = module;
  const data = module.data || {};
  const sliderRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [stageRef, tallestSlide] = useTallestSlide(features, data.crop);

  useEffect(() => {
    if (activeIndex < features.length) return;
    setActiveIndex(0);
    sliderRef.current?.slickGoTo(0, true);
  }, [activeIndex, features.length]);

  if (!features.length) return null;

  const selectedBackground = backgroundValue(data, 'selectablebg');
  const normalizedBackground = typeof selectedBackground === 'string' && !selectedBackground.includes('/') ? `/sets/images/${selectedBackground}.jpg` : selectedBackground;
  const style = {
    ...backgroundStyle(normalizedBackground, { size: data.backgroundSize || 'cover', position: data.backgroundPosition || 'center', tileSize: data.backgroundTileSize }),
    backgroundColor: data.backgroundColor || '#17191f',
    '--section-overlay': backgroundOverlay(data, 25),
  };
  const ratio = { '1': '16 / 9', '2': '4 / 3', '3': '1 / 1' }[data.crop] || 'auto';
  const multiple = features.length > 1;
  const settings = {
    accessibility: true,
    adaptiveHeight: false,
    arrows: multiple,
    autoplay: multiple && Boolean(data.autoplay),
    autoplaySpeed: 8000,
    beforeChange: (_current, next) => setActiveIndex(next),
    dots: multiple,
    dotsClass: 'set-story-dots',
    draggable: multiple,
    infinite: multiple,
    nextArrow: <StoryArrow direction="next" />,
    pauseOnDotsHover: true,
    pauseOnFocus: true,
    pauseOnHover: true,
    prevArrow: <StoryArrow direction="previous" />,
    slidesToScroll: 1,
    slidesToShow: 1,
    speed: 450,
    swipe: multiple,
    touchMove: multiple,
    waitForAnimate: false,
    customPaging: (index) => <button type="button" aria-label={`Show story slide ${index + 1}`} aria-current={activeIndex === index ? 'true' : undefined} />,
  };

  return (
    <section className="set-presentation-section set-story-slider" style={style}>
      <div className="set-presentation-shade" />
      <div className="container set-wide-section">
        {(data.title || data.intro) && <header className="set-section-heading text-center">{data.title && <h2>{data.title}</h2>}{data.intro && <p>{data.intro}</p>}</header>}
        <div ref={stageRef} className={`set-story-stage ${multiple ? 'set-has-multiple' : ''}`} style={tallestSlide ? { '--story-slide-height': `${tallestSlide}px` } : undefined}>
          <Slider ref={sliderRef} className="set-story-carousel" {...settings}>
            {features.map((feature, index) => {
              const item = feature.data || {};
              const image = imageUrl(item.background);
              return (
                <div className="set-story-slide-shell" key={feature.id || index}>
                  <article className={`set-story-slide ${item.mirror ? 'set-image-right' : ''} ${image ? 'set-has-image' : 'set-without-image'}`}>
                    {image && <div className="set-story-slide-media"><MediaFrame type={item.mediaFrame}><button type="button" className="set-story-slide-image" onClick={() => onImageClick?.(image)}><img src={image} alt={item.title || 'Story'} style={{ aspectRatio: ratio }} /></button></MediaFrame></div>}
                    <div className="set-story-slide-copy">{item.title && <h3>{item.title}</h3>}{item.content && <div dangerouslySetInnerHTML={{ __html: cleanHtml(item.content) }} />}</div>
                  </article>
                </div>
              );
            })}
          </Slider>
        </div>
      </div>
    </section>
  );
}
